import * as React from "react";
import { forwardRef, useMemo, useCallback } from "react";
import { useTheme } from "styled-components";
import StyledAceEditor from "./StyledAceEditor";
const ACE_THEME = "ace-queryinput";
const isEnabledInput = (props) => !props.disabled;
const isDisabledInput = (props) => props.disabled;
const getMarkers = (errors, warnings) => {
  const markerClassName = "ace_marker";
  const createMarkers = (explanations = [], className = "") => explanations.map(({ beginLine, beginColumn, endLine, endColumn }) => ({
    startRow: beginLine,
    startCol: beginColumn,
    endRow: endLine,
    endCol: endColumn,
    type: "text",
    className
  }));
  return [
    ...createMarkers(errors == null ? void 0 : errors.explanations, `${markerClassName} ace_validation_error`),
    ...createMarkers(warnings == null ? void 0 : warnings.explanations, `${markerClassName} ace_validation_warning`)
  ];
};
const BasicQueryInput = forwardRef((props, ref) => {
  const {
    className = "",
    disabled = false,
    error,
    height,
    maxLines = 4,
    placeholder = "",
    value = "",
    warning,
    wrapEnabled = true,
    onLoad,
    inputId
  } = props;
  const theme = useTheme();
  const markers = useMemo(() => getMarkers(error, warning), [error, warning]);
  const _onLoad = useCallback(
    (editor) => {
      if (editor) {
        editor.renderer.setScrollMargin(7, 6);
        editor.renderer.setPadding(12);
        if (inputId) {
          editor.textInput.getElement().setAttribute("id", inputId);
        }
        onLoad == null ? void 0 : onLoad(editor);
      }
    },
    [inputId, onLoad]
  );
  const editorProps = useMemo(() => ({ $blockScrolling: Infinity, selectionStyle: "line" }), []);
  const setOptions = useMemo(() => ({ indentedSoftWrap: false }), []);
  const commonProps = {
    $height: height,
    $scTheme: theme,
    theme: ACE_THEME,
    className: `${className} ${ACE_THEME}`,
    disabled,
    editorProps,
    fontSize: theme.fonts.size.small,
    highlightActiveLine: false,
    markers,
    maxLines,
    minLines: 1,
    mode: "lucene",
    name: "QueryEditor",
    placeholder,
    readOnly: disabled,
    ref,
    setOptions,
    showGutter: false,
    showPrintMargin: false,
    value,
    wrapEnabled,
    onLoad: _onLoad
  };
  if (isDisabledInput(props)) {
    return /* @__PURE__ */ React.createElement(StyledAceEditor, { ...commonProps, disabled: true });
  }
  if (isEnabledInput(props)) {
    const { onBlur, onChange, enableAutocompletion = false } = props;
    return /* @__PURE__ */ React.createElement(
      StyledAceEditor,
      {
        ...commonProps,
        enableBasicAutocompletion: enableAutocompletion,
        enableLiveAutocompletion: enableAutocompletion,
        onBlur,
        onChange
      }
    );
  }
  return null;
});
export default React.memo(BasicQueryInput);
